import {
  Auth0Config,
  LoginGlobalError,
  LoginFieldError,
  JWT,
  OidcContext,
  Action,
} from '../../types';
import hasNoInjectedServerState from '../has-no-injected-server-state';
import {
  BaseRedirectState,
  BasePageState,
  usePageState,
  isRedirectResult,
  isPageStateResult,
} from './usePageState';
import { useAppDispatch, useAppSelector } from '../../stores/redux.store';
import { State as CobrandingState } from '../../reducers/cobranding-reducer';
import { loginSetState } from '../../actions/login-actions';
import { useEffect } from 'react';

interface LoginPageAppConfig {
  googleAuthClientId: string;
  auth0Config: Auth0Config;
  recaptchaEnable: boolean;
  recaptchaEnterpriseCheckboxKeySite: string;
}

export type LoginStateResult = BaseRedirectState | LoginPageState;

export interface LoginPageState extends BasePageState {
  appConfig: LoginPageAppConfig;
  hashedCsrfToken: string;
  cobranding: CobrandingState;
  csrfToken: string;
  login?: {
    error?: LoginGlobalError;
    fieldErrors?: LoginFieldError;
    recoveryToken?: JWT;
  };
  oidcContext?: OidcContext;
}

const usePreloadedState = () => {
  const appConfig = useAppSelector(state => state.appConfig);
  const csrfToken = useAppSelector(state => state.csrfToken);
  const cobranding = useAppSelector(state => state.cobranding);
  const hashedCsrfToken = useAppSelector(state => state.hashedCsrfToken);
  const login = useAppSelector(state => state.login);
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);

  return {
    appConfig,
    csrfToken,
    cobranding,
    hashedCsrfToken,
    login,
    oidcContext,
  };
};

export const useLoginState = () => {
  const noServerState = hasNoInjectedServerState();
  const dispatch = useAppDispatch();

  const { state: stateFromApi, isLoading, isError, error } = usePageState('/login/state', {
    enabled: noServerState,
  });

  useEffect(() => {
    if (isPageStateResult(stateFromApi)) {
      dispatch(loginSetState(stateFromApi));
    }
  }, [dispatch, stateFromApi]);

  const preloadedState = usePreloadedState();

  if (isRedirectResult(stateFromApi)) {
    window.location.assign(stateFromApi.uri);

    return {
      state: undefined,
      isLoading: true,
      isError: false,
    };
  }

  if (noServerState) {
    return {
      state: stateFromApi,
      isLoading,
      isError,
      error,
    };
  }

  return {
    state: preloadedState,
    isLoading: false,
    isError: false,
    error,
  };
};
